import { useEffect } from "react";
import { get, useFieldArray, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";

import testIds from "constants/testIds";
import { ReactComponent as PlusIcon } from "assets/images/iconsV2/plus.svg";
import Button from "components/v2/Button/Button";
import Divider from "components/v2/Divider/Divider";
import { StopType } from "types/MultiStop";
import { getDefaultPickupStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import { useIsQuickRequest } from "@RequestFormV2/context/requestFormMode";
import { usePersistDraft } from "@RequestFormV2/hooks/usePersistDraft";
import { useCollapseExtraStopsWhenMultistopDisabled } from "@RequestFormV2/sections/Schedule/hooks/useCollapseExtraStopsWhenMultistopDisabled";
import { getStopFieldPath } from "@RequestFormV2/sections/Schedule/utils/fieldPaths";
import { AT_LEAST_ONE_DELIVERY_ERROR_PATH } from "@RequestFormV2/sections/Schedule/validation/validateSchedule";
import { useSchedule } from "@RequestFormV2/sections/Schedule/context";
import StopCard from "@RequestFormV2/sections/Schedule/components/StopCard";
import StopDivider from "@RequestFormV2/sections/Schedule/components/StopDivider";
import MultistopInstructions from "@RequestFormV2/sections/Schedule/components/MultistopInstructions";
import ScheduleFieldErrorBadge from "@RequestFormV2/components/Badges/ScheduleFieldErrorBadge";

export default function StopList() {
  const intl = useIntl();
  const { control, getValues, setValue, formState } = useFormContext();
  const { fields, append, insert, remove, replace } = useFieldArray({
    control,
    name: "schedule.stops",
  });
  const persistDraft = usePersistDraft();
  const isQuickRequest = useIsQuickRequest();
  const { scheduleOptions } = useSchedule();
  useCollapseExtraStopsWhenMultistopDisabled(replace);

  const canAddStop = !isQuickRequest && !!scheduleOptions?.multi_stops_enabled;

  const atLeastOneDeliveryError = get(formState.errors, AT_LEAST_ONE_DELIVERY_ERROR_PATH);

  const numberOfStops = fields.length;
  const isMultiStop = numberOfStops > 2;

  // A plain pickup/delivery request keeps the second stop as the delivery.
  useEffect(() => {
    if (numberOfStops <= 2) {
      setValue(getStopFieldPath(1, "stop_type"), StopType.DROPOFF);
    }
  }, [numberOfStops, setValue]);

  const handleAddStop = () => {
    append(getDefaultPickupStopValue());
    persistDraft("schedule");
    // Match v1: drop focus from the add button after it triggers a layout change.
    setTimeout(() => {
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    }, 0);
  };

  const handleInsertStop = (index: number) => {
    insert(index, getDefaultPickupStopValue());
    persistDraft("schedule");
  };

  const handleDuplicateStop = (index: number) => {
    const source = getValues(`schedule.stops.${index}`);
    insert(index + 1, { ...source, stop_key: crypto.randomUUID() });
    persistDraft("schedule");
  };

  const handleRemoveStop = (index: number) => {
    if (numberOfStops <= 2) return;
    remove(index);
    // WEB-11639: classic pickup + delivery must not keep packing-list stop keys.
    if (numberOfStops - 1 <= 2) {
      const packingList = (getValues("packing_list") as unknown[]) || [];
      packingList.forEach((_, packingIndex) => {
        setValue(`packing_list.${packingIndex}.pickup_stop_key`, undefined, { shouldDirty: true });
        setValue(`packing_list.${packingIndex}.delivery_stop_key`, undefined, { shouldDirty: true });
      });
      persistDraft("packing_list");
    }
    persistDraft("schedule");
  };

  return (
    <div className="rfv2-schedule" data-testid={testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_SECTION}>
      {fields.map((field, index) => (
        <div key={field.id}>
          {index > 0 &&
            (canAddStop ? (
              <StopDivider
                onInsert={() => handleInsertStop(index)}
                testId={testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_INSERT_STOP_BUTTON}
              />
            ) : (
              <Divider className="mb-3xl" />
            ))}
          <StopCard
            index={index}
            isMultiStop={isMultiStop}
            canRemove={isMultiStop}
            onDuplicate={handleDuplicateStop}
            onRemove={handleRemoveStop}
          />
        </div>
      ))}
      {atLeastOneDeliveryError?.message && (
        <div className="rfv2-schedule-multistop-error">
          <ScheduleFieldErrorBadge message={String(atLeastOneDeliveryError.message)} />
        </div>
      )}
      {canAddStop && (
        <Button
          variant="secondary-color"
          size="sm"
          iconLeft={<PlusIcon />}
          label={intl.formatMessage({ id: "request-form.schedule.addStop", defaultMessage: "Add stop" })}
          testId={testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_ADD_STOP_BUTTON}
          className="rfv2-schedule-add-stop"
          onClick={handleAddStop}
        />
      )}
      {!isQuickRequest && <MultistopInstructions />}
    </div>
  );
}
